// News.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaNewspaper,
  FaArrowRight,
  FaCalendarAlt,
  FaUser,
  FaUserPlus,
  FaSearch,
  FaFilter,
  FaFire,
  FaMapMarkerAlt,
  FaTag,
  FaChevronLeft,
  FaChevronRight,
  FaBookOpen,
  FaShareAlt,
  FaFacebookF,
  FaTwitter,
  FaWhatsapp,
  FaLinkedinIn,
  FaLink,
  FaBookmark,
  FaTimes
} from 'react-icons/fa';
import { getNews, getNewsCategories, getNewsForCarousel } from '../../firebase/firestoreService';
import { programService, Program } from '../../firebase/programFirestoreService';
import { cache } from '../../utils/cacheManager';
import { formatDate, getRandomReadTime } from '../public/home/utils/homeHelpers';
import SeoHead from '../../components/seo/SeoHead';
import { SITE_NAME, SITE_URL } from '../../utils/seo';

// Ensure image URLs include sensible sizing & format params for faster mobile loads
const ensureOptimizedImage = (rawUrl: string | undefined | null, defaultUrl: string, width = 1200) => {
  try {
    const url = rawUrl && typeof rawUrl === 'string' && rawUrl.trim() ? rawUrl : defaultUrl;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}auto=format&fit=crop&w=${width}&q=70&fm=webp`;
  } catch (e) {
    return defaultUrl;
  }
};

interface NewsArticle {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  imageUrl: string;
  date: string;
  category: string;
  author: string;
  isPublished: boolean;
  showInCarousel: boolean;
  showInCards: boolean;
  showInGeneral: boolean;
  tags: string[];
  views: number;
  createdAt: string;
  updatedAt: string;
}

const News: React.FC = () => {
  const navigate = useNavigate();
  const [headlineNews, setHeadlineNews] = useState<NewsArticle[]>([]);
  const [latestProgram, setLatestProgram] = useState<Program | null>(null);
  const [programsForCarousel, setProgramsForCarousel] = useState<Program[]>([]);
  const [programsList, setProgramsList] = useState<Program[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState<boolean>(true);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);
  const now = new Date();
  const [allNews, setAllNews] = useState<NewsArticle[]>([]);
  const [filteredNews, setFilteredNews] = useState<NewsArticle[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState({
    headlines: true,
    news: true,
    categories: true
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  
  const [bookmarkedArticles, setBookmarkedArticles] = useState<string[]>([]);
  const [activeShareId, setActiveShareId] = useState<string | null>(null);
  const [lastDoc, setLastDoc] = useState<any | null>(null);
  const [hasMore, setHasMore] = useState(true);
  
  const articlesPerPage = 6;

  const backendPageSize = 24;

  // Fetch data on component mount
  useEffect(() => {
    fetchNewsData();
    fetchLatestProgram();
    fetchProgramsList();
  }, []);

  const fetchProgramsList = async () => {
    try {
      setLoadingPrograms(true);
      // Always show only the latest 2 published programs on the News page
      const res = await cache.get('published_programs_latest_2', async () => programService.getPublishedPrograms(2), 60 * 60 * 1000);
      if (res && Array.isArray(res)) {
        setProgramsList(res.slice(0, 2));
      } else {
        setProgramsList([]);
      }
    } catch (e) {
      console.error('Error fetching programs list:', e);
      setProgramsList([]);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const parseDate = (date: any): Date | null => {
    if (!date) return null;
    try {
      if (date instanceof Date) return date;
      // Firestore Timestamp
      if (date?.toDate && typeof date.toDate === 'function') return date.toDate();
      // Plain object with seconds (serialized timestamp)
      if (typeof date === 'object' && date !== null && 'seconds' in date && typeof date.seconds === 'number') {
        return new Date(date.seconds * 1000);
      }
      if (typeof date === 'string' || typeof date === 'number') return new Date(date);
      return null;
    } catch (error) {
      return null;
    }
  };

  const getProgramStatus = (program: Program) => {
  const startDate = parseDate(program.startDate);
  const endDate = parseDate(program.endDate);
  const programStatus = program.status || 'published';
  
  // First check manually set statuses
  if (programStatus === 'cancelled') {
    return { status: 'cancelled', label: 'Cancelled', color: 'text-red-700', bgColor: 'bg-red-100' };
  }
  
  if (programStatus === 'completed') {
    return { status: 'completed', label: 'Completed', color: 'text-gray-700', bgColor: 'bg-gray-100' };
  }
  
  // Calculate based on dates
  if (!startDate) {
    return { status: 'published', label: 'Published', color: 'text-blue-700', bgColor: 'bg-blue-100' };
  }
  
  const now = new Date();
  
  if (now < startDate) {
    return { status: 'upcoming', label: 'Upcoming', color: 'text-purple-700', bgColor: 'bg-purple-100' };
  }
  
  if (endDate && now > endDate) {
    return { status: 'completed', label: 'Completed', color: 'text-gray-700', bgColor: 'bg-gray-100' };
  }
  
  // If no end date but has started, or currently within date range
  if ((startDate && !endDate && now >= startDate) || 
      (startDate && endDate && now >= startDate && now <= endDate)) {
    return { status: 'ongoing', label: 'Ongoing', color: 'text-green-700', bgColor: 'bg-green-100' };
  }
  
  return { status: 'published', label: 'Published', color: 'text-blue-700', bgColor: 'bg-blue-100' };
};


const isRegistrationClosed = (program: Program) => {
  const regEnd = parseDate(program.registrationEnd || program.registrationDeadline);
  return !!(regEnd && now > regEnd);
};

const isRegistrationNotYetOpen = (program: Program) => {
  const regStart = parseDate((program as any).registrationStart);
  return !!(regStart && now < regStart);
};

const getRegistrationLabel = (program: Program) => {
  if (!program.requiresRegistration) return 'Not required';
  if (isRegistrationNotYetOpen(program)) return 'Not Yet Opened';
  if (isRegistrationClosed(program)) return 'Closed';
  if (getProgramStatus(program).status === 'completed') return 'Ended';
  return 'Open';
};

const getRegistrationClass = (program: Program) => {
  if (!program.requiresRegistration) return 'text-gray-400';
  if (isRegistrationNotYetOpen(program)) return 'text-amber-300';
  if (isRegistrationClosed(program)) return 'text-red-400';
  if (getProgramStatus(program).status === 'completed') return 'text-gray-400';
  return 'text-green-400';
};

const canRegisterNow = (program: Program) => {
  if (!program.requiresRegistration) return false;
  if (isRegistrationNotYetOpen(program)) return false;
  if (isRegistrationClosed(program)) return false;
  const status = getProgramStatus(program).status;
  return status !== 'completed' && status !== 'cancelled';
};
  // Calculate days until/until completion and progress (similar to Programs.tsx)
  const getDateInfo = (program: Program) => {
    const now = new Date();
    const startDate = parseDate((program as any).startDate);
    const endDate = parseDate((program as any).endDate);

    if (!startDate) return { daysUntil: null, daysTotal: null, percentageComplete: null };

    const daysUntil = Math.ceil((startDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    let daysTotal: number | null = null;
    let percentageComplete: number | null = null;

    if (endDate) {
      daysTotal = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

      if (now >= startDate && now <= endDate) {
        const elapsedDays = Math.ceil((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        percentageComplete = Math.min(Math.round((elapsedDays / (daysTotal || 1)) * 100), 100);
      } else if (now > endDate) {
        percentageComplete = 100;
      }
    }

    return { daysUntil, daysTotal, percentageComplete };
  };

const formatCardDate = (date: any): string => {
  const parsedDate = parseDate(date);
  if (!parsedDate || isNaN(parsedDate.getTime())) return 'TBA';
  return parsedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (date: any): string => {
  if (!date) return '';
  // Handle time-only strings like "08:00" or "8:30:00"
  if (typeof date === 'string') {
    const timeOnlyMatch = date.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (timeOnlyMatch) {
      const hours = Number(timeOnlyMatch[1]);
      const minutes = Number(timeOnlyMatch[2]);
      const seconds = timeOnlyMatch[3] ? Number(timeOnlyMatch[3]) : 0;
      const d = new Date();
      d.setHours(hours, minutes, seconds, 0);
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    }
  }

  const parsedDate = parseDate(date);
  if (!parsedDate || isNaN(parsedDate.getTime())) return '';
  return parsedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

  const fetchLatestProgram = async () => {
    try {
      // Only fetch published programs for the public carousel
      const res = await cache.get('latest_programs_3', async () => programService.getPublishedPrograms(3), 60 * 60 * 1000);
      if (res && Array.isArray(res) && res.length > 0) {
        // Prefer upcoming/ongoing programs first for the carousel
        try {
          const preferred = res.filter((r: Program) => {
            const s = getProgramStatus(r).status;
            return s === 'upcoming' || s === 'ongoing';
          });

          const chosen = preferred.length > 0 ? preferred : res;
          setProgramsForCarousel(chosen.slice(0, 3));
          setLatestProgram(chosen[0]);
          setCarouselIndex(0);
        } catch (e) {
          setProgramsForCarousel(res.slice(0, 3));
          setLatestProgram(res[0]);
        }
      } else {
        setProgramsForCarousel([]);
        setLatestProgram(null);
      }
    } catch (e) {
      console.error('Error fetching latest program:', e);
    }
  };

  // Auto-advance carousel every 7 seconds when there are multiple slides
  useEffect(() => {
    if (!programsForCarousel || programsForCarousel.length <= 1) return;
    const t = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % programsForCarousel.length);
    }, 7000);
    return () => clearInterval(t);
  }, [programsForCarousel]);

  const prevSlide = () => {
    if (!programsForCarousel || programsForCarousel.length <= 1) return;
    setCarouselIndex((prev) => (prev - 1 + programsForCarousel.length) % programsForCarousel.length);
  };

  const nextSlide = () => {
    if (!programsForCarousel || programsForCarousel.length <= 1) return;
    setCarouselIndex((prev) => (prev + 1) % programsForCarousel.length);
  };

  const fetchNewsData = async () => {
    try {
      setLoading({ headlines: true, news: true, categories: true });
      
      // Fetch headline news (cached)
      const headlinesResult = await cache.get(`home_carousel_news_3`, async () => getNewsForCarousel(3), 60 * 60 * 1000);
      if (headlinesResult && headlinesResult.success && headlinesResult.data) {
        const headlines = headlinesResult.data as NewsArticle[];
        setHeadlineNews(headlines);
      }
      setLoading(prev => ({ ...prev, headlines: false }));

      // Fetch paginated news (initial page)
      const newsResult = await getNews({ onlyPublished: true, limitCount: backendPageSize, page: 1 });
      if (newsResult && newsResult.success && newsResult.data) {
        const newsData = newsResult.data as NewsArticle[];
        setAllNews(newsData);
        setFilteredNews(newsData);
        // save pagination cursor if provided by service
        if ((newsResult as any).lastDoc) {
          setLastDoc((newsResult as any).lastDoc);
          setHasMore(true);
        } else {
          setLastDoc(null);
          setHasMore(false);
        }
      }
      setLoading(prev => ({ ...prev, news: false }));

      // Fetch categories (cached)
      const categoriesResult = await cache.get('news_categories', async () => getNewsCategories(), 24 * 60 * 60 * 1000);
      if (categoriesResult && categoriesResult.success && categoriesResult.data) {
        setCategories(categoriesResult.data);
      }
      setLoading(prev => ({ ...prev, categories: false }));

      // Load bookmarks from localStorage
      const savedBookmarks = localStorage.getItem('news_bookmarks');
      if (savedBookmarks) {
        setBookmarkedArticles(JSON.parse(savedBookmarks));
      }
    } catch (error) {
      console.error('Error fetching news data:', error);
      setLoading({ headlines: false, news: false, categories: false });
    }
  };

  // Filter and sort news
  useEffect(() => {
    let filtered = [...allNews];
    const normalizedTerm = searchTerm.trim().toLowerCase();

    // Apply search filter
    if (normalizedTerm) {
      filtered = filtered.filter((article) => {
        const tags = Array.isArray(article.tags) ? article.tags : [];
        return (
          (article.title || '').toLowerCase().includes(normalizedTerm) ||
          (article.excerpt || '').toLowerCase().includes(normalizedTerm) ||
          (article.content || '').toLowerCase().includes(normalizedTerm) ||
          (article.category || '').toLowerCase().includes(normalizedTerm) ||
          (article.author || '').toLowerCase().includes(normalizedTerm) ||
          tags.some((tag) => typeof tag === 'string' && tag.toLowerCase().includes(normalizedTerm))
        );
      });
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    // Apply tag filter
    if (selectedTag) {
      filtered = filtered.filter(article => article.tags.includes(selectedTag));
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        case 'oldest':
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        default:
          return 0;
      }
    });

    setFilteredNews(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [allNews, searchTerm, selectedCategory, selectedTag, sortBy]);

  // Get unique tags from all news
  const allTags = Array.from(new Set(allNews.flatMap(article => article.tags))).filter(tag => tag);
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredPrograms = !normalizedSearchTerm
    ? programsList
    : programsList.filter((program) => {
        const p = program as any;
        const tags = Array.isArray(p.tags) ? p.tags : [];
        return (
          (p.title || '').toLowerCase().includes(normalizedSearchTerm) ||
          (p.description || '').toLowerCase().includes(normalizedSearchTerm) ||
          (p.excerpt || '').toLowerCase().includes(normalizedSearchTerm) ||
          (p.category || '').toLowerCase().includes(normalizedSearchTerm) ||
          (p.location || '').toLowerCase().includes(normalizedSearchTerm) ||
          tags.some((tag: string) => typeof tag === 'string' && tag.toLowerCase().includes(normalizedSearchTerm))
        );
      });

  // Calculate pagination
  const totalPages = Math.ceil(filteredNews.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const endIndex = startIndex + articlesPerPage;
  // Default view shows only the newest N articles (older ones hidden).
  const defaultVisibleCount = 6;
  // Paginate through filtered news by default
  const displayedArticles = filteredNews.slice(startIndex, endIndex);
  const showPagination = totalPages > 1;

  const showingFrom = filteredNews.length === 0 ? 0 : startIndex + 1;
  const showingTo = filteredNews.length === 0 ? 0 : Math.min(endIndex, filteredNews.length);

  // Load more (backend paginated) for default view
  const loadMore = async () => {
    if (!hasMore || !lastDoc) return;
    try {
      setLoading(prev => ({ ...prev, news: true }));
      const res = await getNews({ onlyPublished: true, lastDoc, limitCount: backendPageSize, page: (currentPage || 1) + 1 });
      if (res && res.success && res.data && res.data.length > 0) {
        const newItems = res.data as NewsArticle[];
        setAllNews(prev => [...prev, ...newItems]);
        setFilteredNews(prev => [...prev, ...newItems]);
        if ((res as any).lastDoc) {
          setLastDoc((res as any).lastDoc);
          setHasMore(true);
        } else {
          setLastDoc(null);
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (e) {
      console.error('Error loading more news', e);
    } finally {
      setLoading(prev => ({ ...prev, news: false }));
    }
  };

  // No carousel: we'll display only the latest headline

  // Bookmark handling
  const toggleBookmark = (id: string) => {
    const updatedBookmarks = bookmarkedArticles.includes(id)
      ? bookmarkedArticles.filter(articleId => articleId !== id)
      : [...bookmarkedArticles, id];
    
    setBookmarkedArticles(updatedBookmarks);
    localStorage.setItem('news_bookmarks', JSON.stringify(updatedBookmarks));
  };

  const getArticleUrl = (article: NewsArticle) => `${window.location.origin}/activities/${article.id}`;

  const openShareLink = async (
    article: NewsArticle,
    platform: 'facebook' | 'twitter' | 'whatsapp' | 'linkedin' | 'copy'
  ) => {
    const url = getArticleUrl(article);
    const title = article.title || 'Activity';
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    const encodedMessage = encodeURIComponent(`${title} ${url}`);

    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
      } catch (e) {
        alert(url);
      }
      setActiveShareId(null);
      return;
    }

    let shareUrl = '';
    if (platform === 'facebook') {
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    } else if (platform === 'twitter') {
      shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
    } else if (platform === 'whatsapp') {
      shareUrl = `https://api.whatsapp.com/send?text=${encodedMessage}`;
    } else if (platform === 'linkedin') {
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer,width=620,height=700');
    }
    setActiveShareId(null);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedTag(null);
    setSortBy('newest');
  };

  useEffect(() => {
    const closeShareMenu = () => setActiveShareId(null);
    document.addEventListener('click', closeShareMenu);
    return () => document.removeEventListener('click', closeShareMenu);
  }, []);

  return (
    <>
      <SeoHead
        title={`Activities | ${SITE_NAME}`}
        description="Explore the latest activities, stories, and community updates from Alhaji Inusah Foundation."
        canonicalUrl={`${SITE_URL}/activities`}
        type="website"
      />
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-900 pt-6 pb-8">
          <div className="absolute inset-0 overflow-hidden">
          <div className="hidden sm:block absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl motion-reduce:animate-none pointer-events-none" aria-hidden="true"></div>
          <div className="hidden sm:block absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl motion-reduce:animate-none pointer-events-none" aria-hidden="true"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm text-cyan-300 rounded-full text-sm font-semibold mb-4">
              <FaNewspaper className="text-xs" />
              Latest Activities & Updates
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Foundation <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">Activities</span>
            </h1>

            <p className="text-base md:text-lg text-gray-300 mb-4">
              Stay informed about our latest activities, achievements, and community impact
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search programs, activities, articles, or topics..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Headline Banner - show latest Program if available, otherwise fallback to latest headline */}
      {(!loading.headlines && (latestProgram || headlineNews.length > 0)) && (
        <div className="py-6 bg-gradient-to-b from-white to-slate-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                <span className="flex items-center gap-2">
                  <FaFire className="text-orange-500" />
                  Featured Activity
                </span>
              </h2>
              <div>
                <Link
                  to="/programs"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-shadow shadow-sm"
                  aria-label="View all programs"
                >
                  View All Programs
                  <FaArrowRight className="ml-1" />
                </Link>
              </div>
            </div>

            <div className="relative">
              {(() => {
                // If we have multiple programs, render a carousel (up to 3 slides)
                if (programsForCarousel && programsForCarousel.length > 0) {
                  return (
                    <div className="relative">
                      <div className="overflow-hidden rounded-2xl shadow-2xl">
                        {programsForCarousel.map((p, idx) => {
                          const status = getProgramStatus(p);
                          const start = parseDate((p as any).startDate);
                          const end = parseDate((p as any).endDate);
                          const active = idx === carouselIndex;
                          return (
                            <motion.div
                              key={p.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: active ? 1 : 0, y: active ? 0 : 10 }}
                              transition={{ duration: 0.5 }}
                              className={`${active ? 'block' : 'hidden'} bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl overflow-hidden`}
                            >
                              <div className="grid md:grid-cols-5 gap-0">
                                <div className="relative w-full md:col-span-3 aspect-[1560/525] md:aspect-auto md:h-full min-h-[220px] md:min-h-0">
                                  {active ? (
                                    <img
                                      className="absolute inset-0 w-full h-full object-cover object-right sm:object-center"
                                      src={ensureOptimizedImage((p as any).imageUrl || '', 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1', 800)}
                                      srcSet={`${ensureOptimizedImage((p as any).imageUrl || '', 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1', 400)} 400w, ${ensureOptimizedImage((p as any).imageUrl || '', 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1', 800)} 800w`}
                                      sizes="(max-width:640px) 100vw, (max-width:1024px) 50vw, 33vw"
                                      loading="lazy"
                                      decoding="async"
                                      alt={(p as any).title || 'Program image'}
                                      fetchPriority={idx === 0 ? 'high' : 'auto'}
                                    />
                                  ) : (
                                    <div className="absolute inset-0 bg-gray-800" aria-hidden="true" />
                                  )}
                                  <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/50 to-transparent"></div>

                                  <div className="absolute top-4 left-4">
                                    <span className="px-3 py-1.5 text-white rounded-full text-xs font-semibold" style={{ background: 'linear-gradient(90deg,#06b6d4,#3b82f6)' }}>
                                      {status.label}
                                    </span>
                                  </div>

                                  <div className="absolute top-4 right-4 flex flex-col items-end gap-2">
                                    {(() => {
                                      const dateInfo = getDateInfo(p);
                                      return (
                                        <>
                                          {dateInfo.daysUntil !== null && dateInfo.daysUntil > 0 && (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-purple-700">
                                              Starts in {dateInfo.daysUntil} day{dateInfo.daysUntil !== 1 ? 's' : ''}
                                            </span>
                                          )}

                                          {dateInfo.percentageComplete !== null && dateInfo.percentageComplete > 0 && (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-green-700">
                                              {dateInfo.percentageComplete}% Complete
                                            </span>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>

                                </div>

                                <div className="p-4 md:p-6 flex flex-col justify-between md:col-span-2">
                                  <div>
                                    {(p as any).category && (
                                      <div className="mb-3">
                                        <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                                          {(p as any).category}
                                        </span>
                                      </div>
                                    )}

                                    <h3 className="text-xl md:text-xl font-bold text-white mb-3 line-clamp-2">
                                      {p.title}
                                    </h3>

                                    <p className="text-gray-300 mb-4 h-[4.5rem] overflow-hidden line-clamp-3">
                                      {(p as any).description || (p as any).excerpt || ''}
                                    </p>

                                    <div className="space-y-3 text-sm text-gray-300">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <FaCalendarAlt />
                                          <div>
                                            <div className="font-medium text-white">
                                              Start - {formatCardDate((p as any).startDate)}{(p as any).startTime ? `  ${formatTime((p as any).startTime)}` : ''}
                                            </div>
                                            { (p as any).endDate && (
                                              <div className="font-medium text-gray-300">End - {formatCardDate((p as any).endDate)}{(p as any).endTime ? `  ${formatTime((p as any).endTime)}` : ''}</div>
                                            )}
                                          </div>
                                        </div>

                                        {((p as any).requiresRegistration) && (
                                          <div className={`px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white`}>
                                            {getProgramStatus(p).label}
                                          </div>
                                        )}
                                      </div>

                                      { (p as any).location && (
                                        <div className="flex items-center gap-2 text-gray-300">
                                          <FaMapMarkerAlt />
                                          <span>{(p as any).location}</span>
                                        </div>
                                      )}

                                      <div className="flex items-center gap-2 text-gray-300">
                                        <FaUserPlus className="text-sm text-gray-300" />
                                        <div className="text-xs">Registration</div>
                                        <div className={`font-medium ${getRegistrationClass(p)}`}>
                                          {getRegistrationLabel(p)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                                    <Link
                                      to={`/programs/${p.id}`}
                                      className="flex items-center text-white/90 hover:text-white font-medium"
                                    >
                                      View Details
                                      <FaArrowRight className="ml-2" />
                                    </Link>

                                    {canRegisterNow(p) && (
                                      <button
                                        onClick={() => navigate(`/programs/${p.id}/register`)}
                                        className={`px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-200 font-medium shadow-sm`}
                                      >
                                        {getProgramStatus(p).status === 'upcoming' ? 'Register Now' : 'Register'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}

                        {programsForCarousel.length > 1 && (
                          <>
                            <button onClick={prevSlide} className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full">
                              <FaChevronLeft />
                            </button>
                            <button onClick={nextSlide} className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full">
                              <FaChevronRight />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                }

                if (latestProgram) {
                  const p = latestProgram;
                  const status = getProgramStatus(p);
                  const start = parseDate((p as any).startDate);
                  const end = parseDate((p as any).endDate);
                  return (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl overflow-hidden shadow-2xl"
                    >
                      <div className="grid md:grid-cols-5 gap-0">
                        <div className="relative w-full md:col-span-3 aspect-[1560/525] md:aspect-auto md:h-full min-h-[220px] md:min-h-0">
                          <img
                            className="absolute inset-0 w-full h-full object-cover object-right sm:object-center"
                            src={ensureOptimizedImage((p as any).imageUrl || '', 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1', 1200)}
                            srcSet={`${ensureOptimizedImage((p as any).imageUrl || '', 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1', 800)} 800w, ${ensureOptimizedImage((p as any).imageUrl || '', 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1', 1200)} 1200w`}
                            sizes="(max-width:640px) 100vw, (max-width:1024px) 50vw, 33vw"
                            loading="lazy"
                            decoding="async"
                            alt={(p as any).title || 'Program image'}
                          />
                          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/50 to-transparent"></div>

                          {/* Status Badge */}
                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1.5 text-white rounded-full text-xs font-semibold" style={{ background: 'linear-gradient(90deg,#06b6d4,#3b82f6)' }}>
                              {status.label}
                            </span>
                          </div>

                          {/* Date / Progress badges */}
                          <div className="absolute top-4 right-4 flex flex-col items-end gap-2">
                            {(() => {
                              const dateInfo = getDateInfo(p);
                              return (
                                <>
                                  {dateInfo.daysUntil !== null && dateInfo.daysUntil > 0 && (
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-purple-700">
                                      Starts in {dateInfo.daysUntil} day{dateInfo.daysUntil !== 1 ? 's' : ''}
                                    </span>
                                  )}

                                  {dateInfo.percentageComplete !== null && dateInfo.percentageComplete > 0 && (
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-green-700">
                                      {dateInfo.percentageComplete}% Complete
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </div>

                        </div>

                        <div className="p-4 md:p-6 flex flex-col justify-between md:col-span-2">
                          <div>
                            {/* Category Tag */}
                            {(p as any).category && (
                              <div className="mb-3">
                                <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                                  {(p as any).category}
                                </span>
                              </div>
                            )}

                            <h3 className="text-xl md:text-2xl font-bold text-white mb-3 line-clamp-2">
                              {p.title}
                            </h3>

                            <p className="text-gray-300 mb-4 h-[4.5rem] overflow-hidden line-clamp-3">
                              {(p as any).description || (p as any).excerpt || ''}
                            </p>

                            {/* Date & Time, Location */}
                            <div className="space-y-3 text-sm text-gray-300">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <FaCalendarAlt />
                                  <div>
                                    <div className="font-medium text-white">
                                      Start - {formatCardDate((p as any).startDate)}{(p as any).startTime ? ` at ${formatTime((p as any).startTime)}` : ''}
                                    </div>
                                    { (p as any).endDate && (
                                      <div className="text-xs text-gray-300">End - {formatCardDate((p as any).endDate)}{(p as any).endTime ? ` at ${formatTime((p as any).endTime)}` : ''}</div>
                                    )}
                                  </div>
                                </div>

                                {((p as any).requiresRegistration) && (
                                  <div className={`px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white`}>
                                    {getProgramStatus(p).label}
                                  </div>
                                )}
                              </div>

                              { (p as any).location && (
                                <div className="flex items-center gap-2 text-gray-300">
                                  <FaMapMarkerAlt />
                                  <span>{(p as any).location}</span>
                                </div>
                              )}

                              <div className="flex items-center gap-2 text-gray-300">
                                <FaUserPlus className="text-sm text-gray-300" />
                                <div className="text-xs">Registration</div>
                                <div className={`font-medium ${getRegistrationClass(p)}`}>
                                  {getRegistrationLabel(p)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center justify-between pt-4 border-t border-white/10">
                            <Link
                              to={`/programs/${p.id}`}
                              className="flex items-center text-white/90 hover:text-white font-medium"
                            >
                              View Details
                              <FaArrowRight className="ml-2" />
                            </Link>

                            {canRegisterNow(p) && (
                              <button
                                onClick={() => navigate(`/programs/${p.id}/register`)}
                                className={`px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-200 font-medium shadow-sm`}
                              >
                                {getProgramStatus(p).status === 'upcoming' ? 'Register Now' : 'Register'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                }

                const article = headlineNews[0];
                return (
                  <motion.div
                    key={article.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl overflow-hidden shadow-2xl"
                  >
                      <div className="grid md:grid-cols-5 gap-0">
                        {/* Image Section */}
                        <div className="relative w-full md:col-span-3 aspect-[1560/525] md:aspect-auto md:h-full min-h-[220px] md:min-h-0">
                            <img
                              className="absolute inset-0 w-full h-full object-cover object-right sm:object-center transform group-hover:scale-110 transition-transform duration-700"
                              src={ensureOptimizedImage(article.imageUrl, 'https://images.unsplash.com/photo-1559028012-481c04fa702d', 800)}
                              srcSet={`${ensureOptimizedImage(article.imageUrl, 'https://images.unsplash.com/photo-1559028012-481c04fa702d', 400)} 400w, ${ensureOptimizedImage(article.imageUrl, 'https://images.unsplash.com/photo-1559028012-481c04fa702d', 800)} 800w`}
                              sizes="(max-width:640px) 100vw, 50vw"
                              loading="lazy"
                              decoding="async"
                              alt={article.title || 'Article image'}
                            />
                          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/50 to-transparent"></div>

                          {/* Category Badge */}
                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full text-xs font-semibold">
                              {article.category}
                            </span>
                          </div>

                        </div>

                        {/* Content Section */}
                        <div className="p-4 md:p-6 flex flex-col justify-between md:col-span-2">
                          <div>
                            <h3 className="text-xl md:text-2xl font-bold text-white mb-3 line-clamp-2">
                              {article.title}
                            </h3>

                            <p className="text-gray-300 mb-4 h-[4.5rem] overflow-hidden line-clamp-3">
                              {article.excerpt}
                            </p>

                            {/* Tags */}
                            <div className="flex flex-wrap gap-2 mb-6">
                              {article.tags.slice(0, 3).map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-white/10 backdrop-blur-sm text-gray-300 rounded text-xs"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2 text-gray-300">
                                <FaCalendarAlt className="text-sm" />
                                <span className="text-sm">
                                  {formatDate(new Date(article.date))}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-300">
                                <FaUser className="text-sm" />
                                <span className="text-sm">Published by {article.author}</span>
                              </div>
                            </div>

                            <Link
                              to={`/activities/${article.id}`}
                              className="group inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 hover:shadow-md"
                            >
                              Read More
                              <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                            </Link>
                          </div>
                        </div>
                      </div>
                  </motion.div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="py-12">
        <div className="container mx-auto px-4">
          <div>
            {/* Horizontal Filters Bar */}
            <div className="mb-8">
              <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <FaFilter className="text-blue-500" />
                  <div className="text-sm font-semibold text-slate-700">Categories:</div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        selectedCategory === 'all'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      All
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          selectedCategory === category
                            ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                  {allTags.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <FaTag className="text-purple-500" />
                      <div className="text-sm font-semibold text-slate-700">Tags:</div>
                      <div className="flex flex-wrap gap-2">
                        {allTags.slice(0, 10).map((tag) => (
                          <button
                            key={tag}
                            onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                            className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                              selectedTag === tag
                                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <div className="text-sm font-semibold text-slate-700">Sort:</div>
                    <div className="flex items-center gap-2">
                      {[{ value: 'newest', label: 'Newest' }, { value: 'oldest', label: 'Oldest' }].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setSortBy(option.value as any)}
                          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                            sortBy === option.value
                              ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {(searchTerm || selectedCategory !== 'all' || selectedTag || sortBy !== 'newest') && (
                    <button
                      onClick={clearFilters}
                      className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-semibold rounded-xl shadow-md transition-all duration-300"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* News Grid */}
            <div>
              {/* Programs Section */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-slate-900">Programs</h2>
                  <Link
                    to="/programs"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-shadow shadow-sm"
                    aria-label="View all programs"
                  >
                    View All Programs
                    <FaArrowRight className="ml-1" />
                  </Link>
                </div>

                {loadingPrograms ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="h-48 bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl animate-pulse"></div>
                    ))}
                  </div>
                ) : programsList.length === 0 ? (
                  <div className="text-center py-8 text-slate-600">No programs available</div>
                ) : filteredPrograms.length === 0 ? (
                  <div className="text-center py-8 text-slate-600">
                    No programs found for "{searchTerm}"
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6 mb-6">
                    {filteredPrograms.map((p) => (
                      <div
                        key={p.id}
                        className="group relative cursor-pointer"
                        tabIndex={0}
                        onClick={() => navigate(`/programs/${p.id}`)}
                        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/programs/${p.id}`);
                          }
                        }}
                      >
                        <div className="absolute -inset-2 bg-gradient-to-r from-blue-100/50 to-cyan-100/50 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                        <div className="relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-slate-200 group-hover:border-blue-300 h-[22rem]">
                          <img
                            className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                            src={ensureOptimizedImage((p as any).imageUrl || '', 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1', 800)}
                            alt={(p as any).title || 'Program image'}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20"></div>

                          <div className="absolute top-3 left-3 right-3 z-10 flex justify-between items-start">
                            <span className="px-3 py-1 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full text-xs font-semibold">
                              {(p as any).category || 'Program'}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${getProgramStatus(p).bgColor} ${getProgramStatus(p).color}`}>
                              {getProgramStatus(p).label}
                            </span>
                          </div>

                          <div className="absolute bottom-0 left-0 right-0 z-10 p-5 bg-gradient-to-t from-black/95 via-black/80 to-transparent">
                            <h3 className="text-lg font-bold text-white mb-3 line-clamp-2" title={(p as any).title}>
                              {(p as any).title}
                            </h3>

                            <p className="text-gray-200 text-sm mb-4 line-clamp-3 min-h-[4.5rem]">{(p as any).description || ''}</p>

                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1 text-white/80 text-xs">
                                <FaCalendarAlt />
                                <span>
                                  {formatCardDate((p as any).startDate)}
                                  {(p as any).endDate ? ` | ${formatCardDate((p as any).endDate)}` : ''}
                                </span>
                              </div>
                              <Link to={`/programs/${p.id}`} className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded-lg font-medium text-sm transition-colors">
                                Details
                                <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Stats Bar */}
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                      Other Activities & Updates
                  </h2>
                  <p className="text-slate-600">
                    Showing {showingFrom}-{showingTo} of {filteredNews.length} articles
                  </p>
                </div>
                
                {bookmarkedArticles.length > 0 && (
                  <Link
                    to="/activities/bookmarks"
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:shadow-md transition-shadow"
                  >
                    <FaBookmark className="text-sm" />
                    {bookmarkedArticles.length} Saved
                  </Link>
                )}
              </div>

              {/* Loading State */}
              {loading.news ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="h-80 bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl animate-pulse"></div>
                  ))}
                </div>
              ) : filteredNews.length === 0 ? (
                <div className="text-center py-16">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-full mb-4">
                    <FaNewspaper className="text-2xl text-blue-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">No Activities Found</h3>
                  <p className="text-slate-600 mb-6">
                    {searchTerm ? `No results for "${searchTerm}"` : 'No activities available at the moment'}
                  </p>
                  <button
                    onClick={clearFilters}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-md transition-shadow"
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <>
                  {/* News Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
                    {displayedArticles.map((article, index) => (
                      <motion.div
                        key={article.id}
                        layout="position"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="group relative cursor-pointer"
                        tabIndex={0}
                        onClick={() => { navigate(`/activities/${article.id}`); }}
                        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/activities/${article.id}`);
                          }
                        }}
                      >
                        <div className="absolute -inset-2 bg-gradient-to-r from-blue-100/50 to-cyan-100/50 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        
                        <div className="relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-slate-200 group-hover:border-blue-300 h-[26rem]">
                          <img
                            className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                            src={ensureOptimizedImage(article.imageUrl, 'https://images.unsplash.com/photo-1559028012-481c04fa702d', 800)}
                            srcSet={`${ensureOptimizedImage(article.imageUrl, 'https://images.unsplash.com/photo-1559028012-481c04fa702d', 400)} 400w, ${ensureOptimizedImage(article.imageUrl, 'https://images.unsplash.com/photo-1559028012-481c04fa702d', 800)} 800w`}
                            sizes="(max-width:640px) 100vw, 50vw"
                            loading="lazy"
                            decoding="async"
                            alt={article.title || 'Article image'}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/20"></div>

                          {/* Category & Bookmark */}
                          <div className="absolute top-3 left-3 right-3 z-10 flex justify-between items-start">
                            <span className="px-3 py-1 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full text-xs font-semibold">
                              {article.category}
                            </span>

                            <button
                              onClick={(e) => { e.stopPropagation(); toggleBookmark(article.id); }}
                              className="p-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-lg transition-colors"
                            >
                              <FaBookmark className={`text-sm ${
                                bookmarkedArticles.includes(article.id)
                                  ? 'text-amber-400 fill-amber-400'
                                  : 'text-white'
                              }`} />
                            </button>
                          </div>

                          {/* Dark shaded details area */}
                          <div className="absolute bottom-0 left-0 right-0 z-10 p-5 bg-gradient-to-t from-black/95 via-black/80 to-transparent">
                            <h3
                              className="text-lg font-bold text-white mb-3 line-clamp-2"
                              title={article.title}
                            >
                              {article.title}
                            </h3>

                            <p className="text-gray-200 text-sm mb-2 line-clamp-3">
                              {article.excerpt}
                            </p>

                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1 text-white/80 text-xs">
                                  <FaCalendarAlt />
                                  <span>{formatDate(new Date(article.date))}</span>
                                </div>
                                <div className="flex items-center gap-1 text-white/80 text-xs">
                                  <FaUser />
                                  <span>{article.author}</span>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <div className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveShareId(prev => prev === article.id ? null : article.id);
                                    }}
                                    className="p-2 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                                    title="Share"
                                  >
                                    <FaShareAlt className="text-xs" />
                                  </button>

                                  {activeShareId === article.id && (
                                    <div
                                      onClick={(e) => e.stopPropagation()}
                                      className="absolute right-0 bottom-10 w-44 rounded-lg bg-black/85 backdrop-blur-sm border border-white/20 p-2 shadow-xl z-20"
                                    >
                                      <button
                                        type="button"
                                        onClick={() => openShareLink(article, 'facebook')}
                                        className="w-full text-left px-2 py-1.5 rounded text-white hover:bg-white/10 text-sm flex items-center gap-2"
                                      >
                                        <FaFacebookF className="text-blue-400" />
                                        Facebook
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => openShareLink(article, 'twitter')}
                                        className="w-full text-left px-2 py-1.5 rounded text-white hover:bg-white/10 text-sm flex items-center gap-2"
                                      >
                                        <FaTwitter className="text-sky-400" />
                                        X / Twitter
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => openShareLink(article, 'whatsapp')}
                                        className="w-full text-left px-2 py-1.5 rounded text-white hover:bg-white/10 text-sm flex items-center gap-2"
                                      >
                                        <FaWhatsapp className="text-green-400" />
                                        WhatsApp
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => openShareLink(article, 'linkedin')}
                                        className="w-full text-left px-2 py-1.5 rounded text-white hover:bg-white/10 text-sm flex items-center gap-2"
                                      >
                                        <FaLinkedinIn className="text-blue-300" />
                                        LinkedIn
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => openShareLink(article, 'copy')}
                                        className="w-full text-left px-2 py-1.5 rounded text-white hover:bg-white/10 text-sm flex items-center gap-2"
                                      >
                                        <FaLink className="text-gray-300" />
                                        Copy Link
                                      </button>
                                    </div>
                                  )}
                                </div>

                                <Link
                                  to={`/activities/${article.id}`}
                                  onClick={(e) => { e.stopPropagation(); }}
                                  className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 text-white font-medium text-sm px-3 py-1.5 rounded-lg transition-colors"
                                >
                                  Details
                                  <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {showPagination && (
                    <div className="flex items-center justify-center gap-2 mt-12">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FaChevronLeft />
                      </button>
                      
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-10 h-10 rounded-lg font-medium ${
                              currentPage === pageNum
                                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                                : 'border border-slate-300 text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FaChevronRight />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter CTA */}
      <div className="py-16 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Stay Updated</h2>
            <p className="text-lg text-slate-600 mb-8">
              Subscribe to our newsletter and never miss an update from our foundation
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-1 px-4 py-3 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-lg">
                Subscribe
              </button>
            </div>
            
            <p className="text-slate-500 text-sm mt-4">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default News;

